import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { Buildings, CircleNotch } from '@phosphor-icons/react';

const fmtQ = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PERIODS = [
  { k: 'today', l: 'Hoy' },
  { k: 'week', l: 'Semana' },
  { k: 'month', l: 'Mes' },
];

const EmpresaBlock = ({ label, d, color }) => (
  <div className={`border rounded-[10px] p-3 ${color === 'emerald' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-blue-500/30 bg-blue-500/5'}`}
    data-testid={`pulso-erp-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <p className={`text-[10px] uppercase font-bold ${color === 'emerald' ? 'text-emerald-600' : 'text-blue-600'}`}>{label}</p>
    <p className="text-xl font-black">{fmtQ(d?.total)}</p>
    <p className="text-[10px] text-[var(--text-muted)]">
      {d?.documentos || 0} docs · 📄 {d?.por_tipo?.factura?.docs || 0} fact. ({fmtQ(d?.por_tipo?.factura?.total)}) · 🚚 {d?.por_tipo?.envio?.docs || 0} env. ({fmtQ(d?.por_tipo?.envio?.total)})
    </p>
    {d?.error && <p className="text-[9px] text-red-500 mt-1">ERP no disponible</p>}
  </div>
);

const BrechaTable = ({ brecha, pctColor }) => (
  <>
    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
      <p className="text-[11px] font-black uppercase text-[var(--text-muted)]">Capturado en CRM vs facturado en ERP (CyH + SA)</p>
      <p className="text-xs font-black" data-testid="brecha-global">
        CRM {fmtQ(brecha.total_crm_q)} / ERP {fmtQ(brecha.total_erp_q)} → <span className={pctColor(brecha.pct_capturado_global)}>{brecha.pct_capturado_global ?? 0}% capturado</span>
      </p>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-[9px] uppercase font-bold text-[var(--text-muted)] border-b border-[var(--border)]">
            <th className="text-left py-1.5 pr-2">Vendedor (ERP)</th>
            <th className="text-right py-1.5 px-2">ERP facturado</th>
            <th className="text-right py-1.5 px-2">CRM reportado</th>
            <th className="text-right py-1.5 pl-2">% capturado</th>
          </tr>
        </thead>
        <tbody>
          {brecha.por_vendedor.map((f) => (
            <tr key={f.vendedor} className="border-b border-[var(--border)]/50">
              <td className="py-1.5 pr-2 font-bold">{f.vendedor}{f.crm_nombre ? <span className="text-[9px] text-[var(--text-faint)] font-normal"> ↔ {f.crm_nombre}</span> : ''}</td>
              <td className="py-1.5 px-2 text-right">{fmtQ(f.erp_q)} <span className="text-[var(--text-faint)]">({f.erp_docs})</span></td>
              <td className="py-1.5 px-2 text-right">{fmtQ(f.crm_q)} <span className="text-[var(--text-faint)]">({f.crm_ventas})</span></td>
              <td className={`py-1.5 pl-2 text-right font-black ${pctColor(f.pct_capturado)}`}>{f.pct_capturado === null ? '—' : `${f.pct_capturado}%`}</td>
            </tr>
          ))}
          {brecha.solo_en_crm.map((f) => (
            <tr key={`crm-${f.vendedor}`} className="border-b border-[var(--border)]/50 opacity-70">
              <td className="py-1.5 pr-2 font-bold">{f.vendedor} <span className="text-[9px] text-[var(--text-faint)] font-normal">(solo CRM)</span></td>
              <td className="py-1.5 px-2 text-right">—</td>
              <td className="py-1.5 px-2 text-right">{fmtQ(f.crm_q)} <span className="text-[var(--text-faint)]">({f.crm_ventas})</span></td>
              <td className="py-1.5 pl-2 text-right">—</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="text-[9px] text-[var(--text-faint)] mt-1.5">{brecha.nota}</p>
  </>
);

export const HarpErpPulso = ({ defaultPeriod = 'today', embedded = false }) => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noAccess, setNoAccess] = useState(false);
  const [showBrecha, setShowBrecha] = useState(false);
  const [brecha, setBrecha] = useState(null);
  const [brechaLoading, setBrechaLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setBrecha(null);
    api.get(`/harperp/pulso-ventas?period=${period}`, { timeout: 120000 })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive && e?.response?.status === 403) setNoAccess(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period]);

  useEffect(() => {
    if (!showBrecha || brecha) return;
    let alive = true;
    setBrechaLoading(true);
    api.get(`/harperp/brecha-crm-erp?period=${period}`, { timeout: 120000 })
      .then((r) => { if (alive) setBrecha(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setBrechaLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBrecha, period, brecha]);

  if (noAccess) return null;

  const pctColor = (p) => (p === null || p === undefined) ? 'text-[var(--text-muted)]' : p >= 80 ? 'text-emerald-500' : p >= 40 ? 'text-amber-500' : 'text-red-500';

  const inner = (
    <>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div>
          {!embedded && (
            <h2 className="text-sm font-black text-[var(--text)] flex items-center gap-1.5">
              <Buildings size={16} weight="fill" className="text-emerald-500" /> Ventas HarpERP · facturación real (ambas empresas)
            </h2>
          )}
          <p className="text-[10px] text-[var(--text-muted)]">
            Fuente oficial ERP{data ? ` · ${data.rango.desde}${data.rango.desde !== data.rango.hasta ? ` a ${data.rango.hasta}` : ''}` : ''} · la meta es que CRM y HarpERP coincidan
          </p>
        </div>
        <div className="flex gap-1 bg-[var(--bg)] border border-[var(--border)] rounded-md p-0.5">
          {PERIODS.map((p) => (
            <button key={p.k} onClick={() => setPeriod(p.k)}
              className={`px-3 py-1 text-[11px] font-bold rounded ${period === p.k ? 'bg-emerald-600 text-white' : 'text-[var(--text-muted)]'}`}
              data-testid={`pulso-erp-period-${p.k}`}>
              {p.l}
            </button>
          ))}
        </div>
      </div>
      {loading && !data ? (
        <p className="text-center py-4 text-xs text-[var(--text-muted)]"><CircleNotch size={16} className="animate-spin inline mr-1" /> Consultando HarpERP…</p>
      ) : data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="pulso-erp-grid">
            <div className="border border-[var(--border)] bg-[var(--surface-muted)] rounded-[10px] p-3">
              <p className="text-[10px] uppercase font-bold text-[var(--text-muted)]">Total combinado</p>
              <p className="text-2xl font-black text-[var(--text)]" data-testid="pulso-erp-total">{fmtQ(data.total_combinado)}</p>
              <p className="text-[10px] text-[var(--text-muted)]">{data.documentos_combinado} documentos ERP</p>
            </div>
            <EmpresaBlock label="Complementos (CyH)" d={data.complementos} color="emerald" />
            <EmpresaBlock label="Soluciones (SA)" d={data.soluciones} color="blue" />
          </div>

          <button onClick={() => setShowBrecha(!showBrecha)}
            className="mt-3 text-[11px] font-bold text-[var(--primary)] hover:underline" data-testid="btn-toggle-brecha">
            {showBrecha ? '▲ Ocultar brecha CRM vs ERP' : '▼ Ver brecha CRM vs ERP por vendedor'}
          </button>
          {showBrecha && (
            <div className="mt-2 border border-[var(--border)] rounded-[10px] p-3 bg-[var(--surface-muted)]" data-testid="brecha-crm-erp">
              {brechaLoading && !brecha ? (
                <p className="text-center py-3 text-xs text-[var(--text-muted)]"><CircleNotch size={14} className="animate-spin inline mr-1" /> Calculando brecha…</p>
              ) : brecha ? <BrechaTable brecha={brecha} pctColor={pctColor} /> : null}
            </div>
          )}
        </>
      )}
    </>
  );

  if (embedded) return <div data-testid="harperp-pulso-card">{inner}</div>;
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4 mb-6" data-testid="harperp-pulso-card">
      {inner}
    </div>
  );
};
